import type { ReactNode } from 'react';
import styled from 'styled-components';
import { decimal, label, projectMonth } from '@entities/ksp/format';
import { Badge } from '@shared/ui/Badge';
import {
  IconBanknote,
  IconBarChart,
  IconInbox,
  IconRuler,
  IconScales,
  IconTarget,
} from '@shared/ui/icons';
import { claimStatusLabel } from '@shared/lib/statusLabels';
import {
  fmtDate,
  fmtMonth,
  fmtNum,
  fmtQty,
  fmtRub,
  fmtShare,
  type BillingBlock,
  type ClaimsBlock,
  type CorrespondenceBlock,
  type KfoProgressBlock,
  type KspBlock,
  type OtherBlock,
  type ProgressKpi,
  type UnitProgress,
} from '@entities/progress';
import {
  Bar,
  BlockCard,
  BlockHead,
  ItemMain,
  ItemMeta,
  ItemTitle,
  Legend,
  LegendDot,
  List,
  ListItem,
  MiniColumns,
  Muted,
  SectionLabel,
  SourceLink,
  Stat,
  StatRow,
} from './parts';

/* Блоки дашборда объекта. Каждый блок — одна тема ТЗ: переписка, претензии,
   прогресс (КФО), выполнено ↔ запроцентовано, КСП, прочие показатели. Числа
   приходят готовыми с бэкенда — здесь только подача. `actions` — место под
   кнопки блока (поручение по показателю). */

/** Сколько единиц измерения показываем в таблицах КФО. */
const UNITS_SHOWN = 6;

interface BlockProps {
  delay: number;
  actions?: ReactNode;
}

/* ── Переписка с Заказчиком ─────────────────────────────────────────────── */

const Direction = styled.span<{ $out: boolean }>`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  color: ${({ $out, theme }) => ($out ? theme.colors.text.muted : theme.colors.primary)};
`;

export function CorrespondenceCard({
  data,
  delay,
  actions,
}: BlockProps & { data: CorrespondenceBlock }) {
  const awaitingTone =
    data.answer_overdue > 0 ? 'danger' : data.awaiting_answer > 0 ? 'warning' : 'neutral';
  return (
    <BlockCard $delay={delay}>
      <BlockHead
        icon={<IconInbox size={18} />}
        title="Переписка с Заказчиком"
        source="ОСНОВА.Диспетчер · почта"
        actions={actions}
      />
      {!data.configured && (
        <Muted>
          Домены почты Заказчика не найдены ни в привязках объекта, ни в почте Диспетчера, а у писем
          нет привязки к договору объекта. Укажите домены в «Привязках объекта».
        </Muted>
      )}
      <StatRow>
        <Stat
          value={fmtNum(data.incoming_recent)}
          label="Получено от Заказчика за 30 дней"
          hint={`всего ${fmtNum(data.incoming_total)}${data.last_incoming_at ? ` · последнее ${fmtDate(data.last_incoming_at)}` : ''}`}
        />
        <Stat
          value={fmtNum(data.outgoing_recent)}
          label="Отправлено Заказчику за 30 дней"
          hint={`всего ${fmtNum(data.outgoing_total)}${data.last_outgoing_at ? ` · последнее ${fmtDate(data.last_outgoing_at)}` : ''}`}
        />
        <Stat
          value={fmtNum(data.awaiting_answer)}
          label="Ждут нашего ответа"
          hint={data.answer_overdue > 0 ? `срок истёк: ${data.answer_overdue}` : 'просроченных нет'}
          tone={awaitingTone}
        />
      </StatRow>
      <div>
        <MiniColumns
          ariaLabel="Письма по месяцам: получено и отправлено"
          data={data.months.map((m) => ({
            key: m.month,
            label: fmtMonth(m.month, false),
            a: m.incoming,
            b: m.outgoing,
            title: `${fmtMonth(m.month)}: получено ${m.incoming}, отправлено ${m.outgoing}`,
          }))}
        />
        <Legend>
          <span>
            <LegendDot />
            получено
          </span>
          <span>
            <LegendDot $second />
            отправлено
          </span>
        </Legend>
      </div>
      {data.recent.length > 0 && (
        <>
          <SectionLabel>Последние письма</SectionLabel>
          <List>
            {data.recent.map((l) => {
              const out = l.direction === 'outgoing';
              return (
                <ListItem key={l.id}>
                  <Direction $out={out} title={out ? 'Отправлено' : 'Получено'}>
                    {out ? '→' : '←'}
                  </Direction>
                  <ItemMain>
                    <ItemTitle title={l.subject}>{l.subject || 'без темы'}</ItemTitle>
                    <ItemMeta title={l.counterpart}>
                      {fmtDate(l.at)} · {l.counterpart || '—'}
                    </ItemMeta>
                  </ItemMain>
                  {l.answer_overdue ? (
                    <Badge $tone="danger">срок ответа истёк</Badge>
                  ) : l.awaiting_answer ? (
                    <Badge $tone="warning">ждёт ответа</Badge>
                  ) : null}
                </ListItem>
              );
            })}
          </List>
        </>
      )}
      {data.truncated && <Muted>Писем больше 5 000 — «всего» посчитано по последним 5 000.</Muted>}
      {data.recent_hidden && (
        <Muted>Список писем виден с доступом к ОСНОВА.Диспетчер или ОСНОВА.Юрист.</Muted>
      )}
      <Muted>
        Исходящие видны, если письмо Заказчику попало в «Отправленные» общего ящика. Письма из
        личных ящиков сотрудников сюда не попадают.
      </Muted>
      <SourceLink to="/agents/dispatcher">Почта Диспетчера</SourceLink>
    </BlockCard>
  );
}

/* ── Претензии ──────────────────────────────────────────────────────────── */

const Inline = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(4)};
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.text.secondary};

  & b {
    color: ${({ theme }) => theme.colors.text.primary};
    font-variant-numeric: tabular-nums;
  }
`;

export function ClaimsCard({ data, delay, actions }: BlockProps & { data: ClaimsBlock }) {
  const allAnswered = data.received > 0 && data.awaiting === 0;
  return (
    <BlockCard $delay={delay}>
      <BlockHead
        icon={<IconScales size={18} />}
        title="Претензии"
        source="ОСНОВА.Юрист · претензионная работа"
        actions={actions}
      />
      {!data.configured && (
        <Muted>
          Заказчика объекта не удалось определить по договорам, а у претензий нет привязки к
          договору объекта. Укажите названия Заказчика в «Привязках объекта».
        </Muted>
      )}
      <StatRow>
        <Stat
          value={fmtNum(data.received)}
          label="Получено претензий"
          hint={
            data.received_amount > 0 ? `требования на ${fmtRub(data.received_amount)}` : undefined
          }
        />
        <Stat
          value={fmtNum(data.answered)}
          label="Отвечено"
          tone={allAnswered ? 'success' : 'neutral'}
        />
        <Stat
          value={fmtNum(data.awaiting)}
          label="Без ответа"
          hint={data.overdue > 0 ? `срок ответа истёк: ${data.overdue}` : undefined}
          tone={data.overdue > 0 ? 'danger' : data.awaiting > 0 ? 'warning' : 'neutral'}
        />
      </StatRow>
      <Inline>
        <span>
          Наши претензии Заказчику: <b>{fmtNum(data.sent)}</b>
        </span>
        <span>
          Иски: <b>{fmtNum(data.lawsuits)}</b>
        </span>
      </Inline>
      {data.recent.length > 0 && (
        <>
          <SectionLabel>Последние</SectionLabel>
          <List>
            {data.recent.map((c) => (
              <ListItem key={c.id}>
                <ItemMain>
                  <ItemTitle title={c.counterparty}>
                    {c.claim_type === 'lawsuit' ? 'Иск' : 'Претензия'}
                    {c.direction === 'outgoing' ? ' (наша)' : ''} · {c.counterparty}
                  </ItemTitle>
                  <ItemMeta>
                    {fmtDate(c.created_at)}
                    {c.amount ? ` · ${fmtRub(c.amount)}` : ''}
                    {c.response_due_at ? ` · ответ до ${fmtDate(c.response_due_at)}` : ''}
                  </ItemMeta>
                </ItemMain>
                <Badge $tone={c.response_overdue ? 'danger' : c.answered ? 'success' : 'neutral'}>
                  {claimStatusLabel(c.stage)}
                </Badge>
              </ListItem>
            ))}
          </List>
        </>
      )}
      {data.recent_hidden && <Muted>Список претензий виден с доступом к ОСНОВА.Юрист.</Muted>}
      <SourceLink to="/agents/statut/claims">Реестр претензий</SourceLink>
    </BlockCard>
  );
}

/* ── Прогресс (КФО Инженера) ────────────────────────────────────────────── */

const Headline = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
`;

const BigPct = styled.div`
  font-size: 40px;
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const HeadlineText = styled.div`
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
  padding-bottom: 2px;

  & b {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const UnitTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;

  & th {
    text-align: right;
    font-size: 11px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text.muted};
    padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(2)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    white-space: nowrap;
  }
  & th:first-child,
  & td:first-child {
    text-align: left;
  }
  & td {
    text-align: right;
    padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    white-space: nowrap;
  }
  & tr:last-child td {
    border-bottom: none;
  }
`;

const BarCell = styled.td`
  width: 32%;
  min-width: 90px;
`;

const TableScroll = styled.div`
  overflow-x: auto;
`;

function unitName(u: UnitProgress): string {
  return u.unit || 'без ед.';
}

export function ProgressCard({
  data,
  projectId,
  delay,
  actions,
}: BlockProps & { data: KfoProgressBlock; projectId: string }) {
  const units = data.units.slice(0, UNITS_SHOWN);
  return (
    <BlockCard $delay={delay}>
      <BlockHead
        icon={<IconRuler size={18} />}
        title="Прогресс по объекту"
        source={`ОСНОВА.Инженер · КФО${data.report_date ? ` · срез от ${fmtDate(data.report_date)}` : ''}`}
        actions={actions}
      />
      {!data.available ? (
        <Muted>
          По объекту нет строк КФО. Загрузите срез Заказчика в ОСНОВА.Инженер (раздел «КФО») и
          привяжите номер договора к объекту — здесь появится факт строительства на сегодня.
        </Muted>
      ) : (
        <>
          <Headline>
            <BigPct>{fmtShare(data.completion_pct)}</BigPct>
            <HeadlineText>
              позиций РД выполнено полностью
              <br />
              <b>{fmtNum(data.lines_completed)}</b> из {fmtNum(data.lines_with_plan)} · начато{' '}
              <b>{fmtNum(data.lines_started)}</b>
            </HeadlineText>
          </Headline>
          <Bar pct={data.completion_pct} tone="success" label="Выполнено позиций РД" />
          <TableScroll>
            <UnitTable>
              <thead>
                <tr>
                  <th>Ед.</th>
                  <th>ФО по РД</th>
                  <th>Факт СМР</th>
                  <th>Выполнено</th>
                  <th aria-hidden />
                </tr>
              </thead>
              <tbody>
                {units.map((u) => (
                  <tr key={u.unit || '—'}>
                    <td>{unitName(u)}</td>
                    <td>{fmtQty(u.fo_total)}</td>
                    <td>{fmtQty(u.fact_cum)}</td>
                    <td>{fmtShare(u.completion_pct)}</td>
                    <BarCell>
                      <Bar pct={u.completion_pct} label={`Выполнено, ${unitName(u)}`} />
                    </BarCell>
                  </tr>
                ))}
              </tbody>
            </UnitTable>
          </TableScroll>
          {data.units.length > UNITS_SHOWN && (
            <Muted>
              Ещё единиц измерения: {data.units.length - UNITS_SHOWN} — полный свод в КФО.
            </Muted>
          )}
        </>
      )}
      <SourceLink to={`/agents/pto/${projectId}/kfo`}>КФО объекта</SourceLink>
    </BlockCard>
  );
}

/* ── Выполнено ↔ запроцентовано ─────────────────────────────────────────── */

export function BillingCard({
  data,
  units,
  delay,
  actions,
}: BlockProps & { data: BillingBlock; units: UnitProgress[] }) {
  return (
    <BlockCard $delay={delay}>
      <BlockHead
        icon={<IconBanknote size={18} />}
        title="Выполнено и запроцентовано"
        source="ОСНОВА.Инженер (КФО) · ОСНОВА.Сметчик (ленты ПА)"
        actions={actions}
      />
      <StatRow>
        <Stat
          value={data.kfo_available ? fmtShare(data.billed_lines_pct) : '—'}
          label="Позиций с фактом принято в ПА целиком"
          hint={
            data.kfo_available
              ? `${fmtNum(data.lines_fully_billed)} из ${fmtNum(data.lines_with_fact)}`
              : 'нет факта в КФО'
          }
        />
        <Stat
          value={data.kfo_available ? fmtNum(data.lines_with_nzp) : '—'}
          label="Позиций с НЗП — выполнено, но не запроцентовано"
          tone={data.lines_with_nzp > 0 ? 'warning' : 'neutral'}
        />
        <Stat
          value={data.ledgers.length > 0 ? fmtRub(data.billed_total) : '—'}
          label="Запроцентовано по ПА (Сметчик)"
          hint={
            data.last_period
              ? `${fmtMonth(data.last_period)}: ${fmtRub(data.last_period_total)}`
              : 'лент ПА по объекту нет'
          }
        />
      </StatRow>
      {data.kfo_available && <BillingUnits units={units} />}
      {data.months.length > 0 && (
        <div>
          <SectionLabel>Процентовка по месяцам, ₽</SectionLabel>
          <MiniColumns
            ariaLabel="Сумма ПА по отчётным месяцам"
            data={data.months.map((m) => ({
              key: m.month,
              label: fmtMonth(m.month, false),
              a: m.total,
              title: `${fmtMonth(m.month)}: ${fmtRub(m.total)}`,
            }))}
          />
        </div>
      )}
      {data.ledgers.length > 0 && (
        <>
          <SectionLabel>
            Ленты ПА{data.ledgers_auto ? ' — подобраны по номерам договоров' : ''}
          </SectionLabel>
          <List>
            {data.ledgers.map((l) => (
              <ListItem key={l.id}>
                <ItemMain>
                  <ItemTitle title={l.object_name}>
                    {l.contract_no} · {l.title || l.object_name}
                  </ItemTitle>
                  <ItemMeta>
                    ПА: {fmtNum(l.pa_count)}
                    {l.last_period ? ` · последний месяц ${fmtMonth(l.last_period)}` : ''}
                  </ItemMeta>
                </ItemMain>
                <b>{fmtRub(l.total_cost)}</b>
              </ListItem>
            ))}
          </List>
        </>
      )}
      <SourceLink to="/agents/smeta/accumulations">Ленты ПА Сметчика</SourceLink>
    </BlockCard>
  );
}

/** Таблица «факт ↔ принято ПА» по единицам (из КФО). */
function BillingUnits({ units }: { units: UnitProgress[] }) {
  const shown = units.filter((u) => u.fact_cum > 0).slice(0, UNITS_SHOWN);
  if (shown.length === 0) return null;
  return (
    <TableScroll>
      <UnitTable>
        <thead>
          <tr>
            <th>Ед.</th>
            <th>Факт СМР</th>
            <th>Принято ПА</th>
            <th>НЗП</th>
            <th>В ПА</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((u) => (
            <tr key={u.unit || '—'}>
              <td>{unitName(u)}</td>
              <td>{fmtQty(u.fact_cum)}</td>
              <td>{fmtQty(u.pa_accepted)}</td>
              <td>{fmtQty(u.nzp)}</td>
              <td>{fmtShare(u.billed_pct)}</td>
            </tr>
          ))}
        </tbody>
      </UnitTable>
    </TableScroll>
  );
}

/* ── План / факт КСП ────────────────────────────────────────────────────── */

const DashedCard = styled(BlockCard)`
  border-style: dashed;
  box-shadow: none;
  background: ${({ theme }) => theme.colors.surface};
`;

export function KspCard({ data, delay, actions }: BlockProps & { data: KspBlock }) {
  const snapshot = data.snapshot;
  return (
    <DashedCard $delay={delay}>
      <BlockHead
        icon={<IconTarget size={18} />}
        title="План и факт по графику"
        source="ОСНОВА.КСП · календарно-сетевое планирование"
        actions={
          <>
            {!data.connected && <Badge $tone="info">нет опубликованного расчёта</Badge>}
            {actions}
          </>
        }
      />
      <Muted>{data.note}</Muted>
      {snapshot && (
        <>
          <StatRow>
            {snapshot.kpis.map((kpi) => (
              <Stat
                key={kpi.code}
                value={decimal(kpi.fact_pct, ' %')}
                label={label(kpi.code.replace('ksp.', ''))}
                hint={`план ${decimal(kpi.plan_pct, ' %')} · отклонение ${decimal(kpi.delta_pp, ' п.п.')}`}
              />
            ))}
          </StatRow>
          <Muted>
            Дата состояния {snapshot.data_date} · {snapshot.timezone} ·{' '}
            {snapshot.coverage === 'partial_rd' ? 'по подтверждённой части РД' : 'полная РД'}.
            Открытых предупреждений в снимке: {snapshot.open_milestone_alert_count}.
          </Muted>
          {snapshot.quality.reasons.map((reason) => (
            <Muted key={reason}>{label(reason)}</Muted>
          ))}
          {snapshot.kpis
            .flatMap((kpi) => kpi.reasons ?? (kpi.reason ? [kpi.reason] : []))
            .map((reason, index) => (
              <Muted key={index}>{label(reason)}</Muted>
            ))}
          <SourceLink
            to={`/agents/ksp/projects/${snapshot.project_id}?tab=planfact&snapshot=${snapshot.snapshot_id}&version=${snapshot.schedule_version_id ?? ''}&period=${snapshot.selected_period ?? projectMonth(snapshot.data_date, snapshot.timezone)}`}
          >
            План-факт в КСП
          </SourceLink>
        </>
      )}
      {data.operational && (
        <Muted>
          Открытых предупреждений сейчас: {data.operational.open_milestone_alert_count}. Оперативный
          срез {data.operational.operational_as_of ?? 'не сформирован'}.
        </Muted>
      )}
      {!snapshot && <SourceLink to="/agents/ksp">Открыть проекты КСП</SourceLink>}
    </DashedCard>
  );
}

/* ── Прочие показатели ──────────────────────────────────────────────────── */

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: ${({ theme }) => theme.spacing(2)};
`;

const KpiItem = styled.div<{ $muted: boolean }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 12.5px;
  opacity: ${({ $muted }) => ($muted ? 0.6 : 1)};

  & b {
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
`;

const GroupBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
`;

function groupTitle(group: string): string {
  switch (group) {
    case 'pto':
      return 'ПТО — физобъёмы и ИД';
    case 'smeta':
      return 'Сметы — освоение по КС-3';
    case 'field':
      return 'Поле — сменная отчётность';
    case 'resources':
      return 'Ресурсы — техника и персонал';
    case 'meetings':
      return 'Совещания — поручения';
    case 'mto':
      return 'Снабжение (МТО) — поставки и заказы';
    case 'logist':
      return 'Логистика — рейсы и заявки';
    case 'geodesy':
      return 'Геодезия — исполнительные схемы';
    case 'requests':
      return 'Межотдельские заявки';
    default:
      return group;
  }
}

function groupSourceLink(group: string): { to: string; label: string } | null {
  switch (group) {
    case 'mto':
      return { to: '/agents/mto/runs?module=supply', label: 'Контроль снабжения МТО' };
    case 'logist':
      return { to: '/agents/logist/orders', label: 'Заявки логистики' };
    case 'geodesy':
      return { to: '/agents/geodesy/schemes', label: 'Исполнительные схемы' };
    case 'requests':
      return { to: '/requests', label: 'Межотдельские заявки' };
    default:
      return null;
  }
}

function kpiValue(k: ProgressKpi): string {
  if (k.needs_review && k.value_fact == null && k.count == null) return 'нет данных';
  if (k.completion_pct != null) return fmtShare(k.completion_pct);
  if (k.value_fact != null) return `${fmtNum(k.value_fact)}${k.unit ? ` ${k.unit}` : ''}`;
  if (k.count != null) return fmtNum(k.count);
  return '—';
}

export function OtherCard({ data, delay, actions }: BlockProps & { data: OtherBlock }) {
  const groups = new Map<string, ProgressKpi[]>();
  for (const k of data.kpis) {
    const arr = groups.get(k.group);
    if (arr) arr.push(k);
    else groups.set(k.group, [k]);
  }
  return (
    <BlockCard $delay={delay}>
      <BlockHead
        icon={<IconBarChart size={18} />}
        title="Паспорт объекта 360° и прочие показатели"
        source="Поле · Совещания · ПТО · Сметы · МТО · Логистика · Геодезия · Заявки"
        actions={actions}
      />
      {Array.from(groups.entries()).map(([group, kpis]) => {
        const link = groupSourceLink(group);
        return (
          <GroupBlock key={group}>
            <SectionLabel>
              {groupTitle(group)}
              {link && (
                <>
                  {' · '}
                  <SourceLink to={link.to}>{link.label}</SourceLink>
                </>
              )}
            </SectionLabel>
            <KpiGrid>
              {kpis.map((k) => (
                <KpiItem
                  key={k.code}
                  $muted={k.needs_review && k.value_fact == null && k.count == null}
                  title={k.note ?? k.formula_note}
                >
                  <div>
                    <span>{k.title}</span>
                    {k.note && <ItemMeta>{k.note}</ItemMeta>}
                  </div>
                  <b>{kpiValue(k)}</b>
                </KpiItem>
              ))}
            </KpiGrid>
          </GroupBlock>
        );
      })}
      {data.warnings.length > 0 && <Muted>{data.warnings.join(' · ')}</Muted>}
    </BlockCard>
  );
}
